import { useEffect, useRef } from "react";
import { AuditEvents } from "./auditLogger";

/**
 * Keyboard Shortcut Protection.
 *
 * Two modes:
 *
 *   captureOnly = false (full guard)
 *       Intercepts application-level shortcuts that could be used to
 *       exfiltrate the document (print, save, copy/cut, select-all,
 *       view-source, devtools) AND watches the screen capture shortcuts.
 *
 *   captureOnly = true (RShield)
 *       Watches ONLY the screen capture shortcuts, and blocks nothing. RShield
 *       has no option to disable printing or copying - those are governed by
 *       the document's own permissions - so it must not intercept them.
 *
 * NOTE: browsers do not guarantee blocking every shortcut. Some browser-reserved
 * shortcuts (e.g., F12 or menu actions) may still work depending on browser
 * policies and user settings. OS level screen capture cannot be blocked at all;
 * the shortcut is detected and reported, never prevented.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {boolean} [opts.captureOnly]  detect capture keys only, block nothing
 * @param {(auditEvent: string) => void} [opts.onBlocked]
 */
export function useKeyboardGuard({ enabled, captureOnly = false, onBlocked } = {}) {
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;

  useEffect(() => {
    if (!enabled) return undefined;

    const report = (auditEvent) => {
      const cb = onBlockedRef.current;
      if (typeof cb === "function") cb(auditEvent);
    };

    /**
     * macOS screen capture combos: Cmd+Shift+3 (full screen), 4 (region),
     * 5 (capture toolbar), 6 (Touch Bar).
     *
     * `event.key` CANNOT be used for these. Holding Shift changes the printable
     * character, so on a US layout Cmd+Shift+3 arrives as key "#", not "3" -
     * and on other layouts it is different again. `code` ("Digit3") and the
     * legacy `keyCode` (51) are both physical-key based and unaffected by Shift
     * or by the keyboard layout, so they are what we match on. The shifted
     * symbols are kept only as a last-resort fallback for browsers that report
     * neither.
     */
    const isMacCaptureCombo = (e) => {
      if (!e.metaKey || !e.shiftKey) return false;
      const digits = ["3", "4", "5", "6"];
      if (e.code && digits.some((d) => e.code === "Digit" + d)) return true;
      const legacy = e.keyCode || e.which;
      if (legacy >= 51 && legacy <= 54) return true;
      // Fallback: the shifted characters produced by those keys on a US layout.
      return ["#", "$", "%", "^"].indexOf(e.key) > -1;
    };

    /**
     * Diagnostic for the macOS combos.
     *
     * macOS may consume Cmd+Shift+3/4/5 in the window server and never deliver
     * it to the browser, in which case no web page can detect it - the event
     * simply never arrives. This logs every Cmd/Meta+Shift key the page DOES
     * receive, so the two cases can be told apart in seconds:
     *
     *   press Cmd+Shift+3 with the console open
     *     -> a "[DocumentProtection][keys]" line appears  = the event arrives,
     *        and if no capture was reported the matching is at fault
     *     -> nothing appears at all                       = macOS swallowed it,
     *        and keyboard detection is impossible for that shortcut
     */
    const logMetaShift = (e, phase) => {
      if (!e.metaKey || !e.shiftKey) return;
      // eslint-disable-next-line no-console
      console.log(
        "[DocumentProtection][keys] " + phase +
        " key=" + JSON.stringify(e.key) +
        " code=" + JSON.stringify(e.code) +
        " keyCode=" + (e.keyCode || e.which) +
        " meta=" + e.metaKey + " shift=" + e.shiftKey +
        " -> capture combo: " + isMacCaptureCombo(e)
      );
    };

    // One physical press can surface as both keydown and keyup - and on macOS
    // the system sometimes delivers only one of them - so both are watched and
    // de-duplicated over a short window.
    let lastCaptureReport = 0;
    const reportCaptureOnce = (source) => {
      const now = Date.now();
      if (now - lastCaptureReport < 700) return;
      lastCaptureReport = now;
      // eslint-disable-next-line no-console
      console.log("[DocumentProtection] Screen capture shortcut detected (" + source + ")");
      report(AuditEvents.SCREENSHOT_ATTEMPT);
    };

    const handler = (e) => {
      logMetaShift(e, "keydown");
      // RShield: capture shortcuts are handled further down; nothing else is
      // intercepted or reported.
      if (captureOnly) {
        if (isMacCaptureCombo(e)) reportCaptureOnce("macOS Cmd+Shift, keydown");
        return undefined;
      }

      const key = (e.key || "").toLowerCase();
      const code = e.keyCode || e.which;
      const ctrlOrMeta = e.ctrlKey || e.metaKey;

      // DevTools / view-source (no modifier combos handled first).
      if (key === "f12" || code === 123) {
        e.preventDefault();
        report(AuditEvents.DEVTOOLS_ATTEMPT);
        return false;
      }

      if (ctrlOrMeta && e.shiftKey) {
        // Ctrl/Cmd + Shift + I / J / C  -> devtools / console / inspect
        if (key === "i" || code === 73) {
          e.preventDefault();
          report(AuditEvents.DEVTOOLS_ATTEMPT);
          return false;
        }
        if (key === "j" || code === 74) {
          e.preventDefault();
          report(AuditEvents.DEVTOOLS_ATTEMPT);
          return false;
        }
        if (key === "c" || code === 67) {
          e.preventDefault();
          report(AuditEvents.DEVTOOLS_ATTEMPT);
          return false;
        }
      }

      if (ctrlOrMeta && !e.shiftKey && !e.altKey) {
        switch (key) {
          case "p": // Print
            e.preventDefault();
            report(AuditEvents.PRINT_ATTEMPT);
            return false;
          case "s": // Save
            e.preventDefault();
            report(AuditEvents.SAVE_ATTEMPT);
            return false;
          case "a": // Select all
            e.preventDefault();
            report(AuditEvents.SELECT_ALL_ATTEMPT);
            return false;
          case "c": // Copy
            e.preventDefault();
            report(AuditEvents.COPY_ATTEMPT);
            return false;
          case "x": // Cut
            e.preventDefault();
            report(AuditEvents.CUT_ATTEMPT);
            return false;
          case "u": // View source
            e.preventDefault();
            report(AuditEvents.VIEW_SOURCE_ATTEMPT);
            return false;
          default:
            break;
        }
      }

      // --- Screen-capture attempts (best-effort: OS-level capture cannot be
      // blocked by the browser; we detect and log for auditing). ---
      if (isMacCaptureCombo(e)) {
        reportCaptureOnce("macOS Cmd+Shift, keydown");
        return undefined;
      }

      return undefined;
    };

    // Windows / Linux: the PrintScreen key typically only fires on keyup in
    // browsers, so it is handled in a dedicated keyup listener below.
    const keyupHandler = (e) => {
      logMetaShift(e, "keyup  ");
      const key = (e.key || "").toLowerCase();
      const code = e.keyCode || e.which;
      if (key === "printscreen" || code === 44) {
        reportCaptureOnce("Print Screen key, keyup");
        return;
      }
      // Second chance for the macOS combos: the system consumes the shortcut,
      // and depending on the browser and OS version the page may see only the
      // keyup. reportCaptureOnce de-duplicates against the keydown.
      if (isMacCaptureCombo(e)) {
        reportCaptureOnce("macOS Cmd+Shift, keyup");
      }
    };

    // Also intercept the print dialog directly (Ctrl+P / menu print). Not in
    // capture-only mode: RShield does not govern printing.
    const beforePrint = () => report(AuditEvents.PRINT_ATTEMPT);

    document.addEventListener("keydown", handler, true);
    document.addEventListener("keyup", keyupHandler, true);
    if (!captureOnly) window.addEventListener("beforeprint", beforePrint, false);

    return () => {
      document.removeEventListener("keydown", handler, true);
      document.removeEventListener("keyup", keyupHandler, true);
      if (!captureOnly) window.removeEventListener("beforeprint", beforePrint, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, captureOnly]);
}

export default useKeyboardGuard;
